import { useState, useEffect, useRef } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useNavigate } from 'react-router-dom';
import { X, Loader2 } from 'lucide-react';
import { initializeProject, startAgentCompose } from '../lib/api';
import { COMPOSER_ASPECT_RATIO_OPTIONS, appendComposerAspectRatioToPrompt } from '../lib/aspectRatioOptions';
import '../LandingPage.css'; // reuse the landing prompt-box styling
import './ComposerModal.css';

/* ── helpers / constants copied to match the landing prompt box exactly ── */

const ASPECT_RATIO_OPTIONS = COMPOSER_ASPECT_RATIO_OPTIONS;

const DEFAULT_ASPECT_RATIO = 'auto';

const MODEL_TIER_OPTIONS = [
  { value: 'fast', name: 'Fast', description: 'A fast and cost-efficient model for composing simple projects.', speed: 4, cost: 3 },
  { value: 'pro', name: 'Pro', description: 'Best for high quality complex projects.', speed: 2, cost: 4.5 },
  { value: 'max', name: 'Max', description: 'Our most capable mode for the most ambitious projects. Uses Claude Fable 5.', speed: 2, cost: 5 },
];

function appendAspectRatioToAgentPrompt(prompt, selectedAspectRatio) {
  return appendComposerAspectRatioToPrompt(prompt, selectedAspectRatio || DEFAULT_ASPECT_RATIO);
}

function getProjectInitAspectRatio(selectedAspectRatio) {
  return selectedAspectRatio || DEFAULT_ASPECT_RATIO;
}

function formatFileSize(bytes) {
  if (bytes === null || bytes === undefined) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function randomId() {
  return Math.random().toString(36).substring(2, 15);
}

function AspectRatioIcon({ w, h, size = 16 }) {
  const maxDim = size * 0.75;
  const scale = maxDim / Math.max(w, h);
  const rw = w * scale;
  const rh = h * scale;
  const x = (size - rw) / 2;
  const y = (size - rh) / 2;
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} className="ar-icon">
      <rect x={x} y={y} width={rw} height={rh} rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
}

function RatingDots({ value, max = 5, size = 6 }) {
  const full = Math.floor(value);
  const hasHalf = value % 1 >= 0.5;
  return (
    <span className="mt-rating-dots">
      {Array.from({ length: max }, (_, i) => {
        if (i < full) {
          return (
            <svg key={i} width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="mt-dot mt-dot--filled">
              <circle cx={size / 2} cy={size / 2} r={size / 2} fill="currentColor" />
            </svg>
          );
        }
        if (i === full && hasHalf) {
          const r = size / 2;
          return (
            <svg key={i} width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="mt-dot mt-dot--half">
              <circle cx={r} cy={r} r={r} fill="none" stroke="currentColor" strokeWidth="0.8" opacity="0.35" />
              <clipPath id={`cm-half-${i}`}><rect x="0" y="0" width={r} height={size} /></clipPath>
              <circle cx={r} cy={r} r={r} fill="currentColor" clipPath={`url(#cm-half-${i})`} />
            </svg>
          );
        }
        return (
          <svg key={i} width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="mt-dot mt-dot--empty">
            <circle cx={size / 2} cy={size / 2} r={size / 2} fill="none" stroke="currentColor" strokeWidth="0.8" opacity="0.35" />
          </svg>
        );
      })}
    </span>
  );
}

const WATCH_TIMER_MS = 10000;

export default function ComposerModal({ reel, analysis, onClose, onDismissAll }) {
  const navigate = useNavigate();

  // phase: 'compose' | 'submitting' | 'started'
  const [phase, setPhase] = useState('compose');
  const [prompt, setPrompt] = useState('');
  const [aspectRatio, setAspectRatio] = useState('9:16'); // reels are vertical
  const [modelTier, setModelTier] = useState('fast');
  const [includeVideo, setIncludeVideo] = useState(true);
  const [includeAnalysis, setIncludeAnalysis] = useState(true);
  const [attached, setAttached] = useState([]); // [{id, file, name, size, extension}]
  const [error, setError] = useState(null);

  const fileInputRef = useRef(null);
  const projectRef = useRef(null);

  const mediaUrl = reel.video_url || reel.thumbnail_url || null;
  const hasAnalysis = Boolean(analysis && analysis.trim());
  const submitting = phase === 'submitting';

  // Auto-dismiss once the agent has started and the timer fills.
  useEffect(() => {
    if (phase !== 'started') return undefined;
    const t = setTimeout(() => {
      if (onDismissAll) onDismissAll();
    }, WATCH_TIMER_MS);
    return () => clearTimeout(t);
  }, [phase, onDismissAll]);

  const handleFileInput = (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    setAttached((prev) => [
      ...prev,
      ...picked.map((f) => ({
        id: randomId(),
        file: f,
        name: f.name,
        size: f.size,
        extension: (f.name.split('.').pop() || 'file').toUpperCase().slice(0, 4),
      })),
    ]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleGenerate = async () => {
    if (submitting) return;
    setPhase('submitting');
    setError(null);
    try {
      const base = prompt.trim() || 'Make a short video inspired by this reel.';
      const analysisBlock =
        includeAnalysis && hasAnalysis
          ? `\n\nHere is an automatically generated analysis of the video:\n${analysis.trim()}`
          : '';

      const projectId = randomId();
      const sessionId = randomId();

      await initializeProject(projectId, base, getProjectInitAspectRatio(aspectRatio));

      const agentPrompt = appendAspectRatioToAgentPrompt(base + analysisBlock, aspectRatio);
      const attachmentUrls = includeVideo && mediaUrl ? [mediaUrl] : [];
      const files = attached.map((a) => a.file);

      const composeResult = await startAgentCompose(
        projectId,
        agentPrompt,
        files,
        modelTier,
        attachmentUrls,
        'reels',
        {
          analyzeVideoAttachments: true,
          displayPrompt: base,
        },
      );

      projectRef.current = {
        projectId,
        navState: {
          agentMode: true,
          agentRunId: composeResult?.run_id || null,
          sessionId,
          promptText: base,
          attachments: [],
          aspectRatio,
          modelTier,
        },
      };
      setPhase('started');
    } catch (err) {
      setError('Failed to start the Composer. Please try again.');
      setPhase('compose');
    }
  };

  const handleWatch = () => {
    const p = projectRef.current;
    if (p) navigate(`/project/${p.projectId}`, { state: p.navState });
  };

  const showGrid = (includeVideo && mediaUrl) || (includeAnalysis && hasAnalysis) || attached.length > 0;

  return (
    <Dialog.Root open onOpenChange={(o) => { if (!o && phase !== 'submitting') onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="cm-overlay" />
        <Dialog.Content className="cm-content" aria-describedby={undefined}>
          <div className="cm-head">
            <Dialog.Title className="cm-title">Use in Composer</Dialog.Title>
            <button className="cm-close" onClick={onClose} aria-label="Close" disabled={submitting}>
              <X size={18} />
            </button>
          </div>

          {phase === 'started' ? (
            <div className="cm-started">
              <div className="cm-started-badge">
                <Loader2 size={20} className="cm-spin" />
              </div>
              <h3 className="cm-started-title">The agent is on it</h3>
              <p className="cm-started-sub">
                Your reel was handed to the Composer agent — it’s already working on your video.
              </p>
              <button type="button" className="cm-watch-btn" onClick={handleWatch}>
                <span className="cm-watch-fill" />
                <span className="cm-watch-label">Watch the Agent work</span>
              </button>
              <p className="cm-started-hint">Closing automatically…</p>
            </div>
          ) : (
            <>
              <section className="landing-upload-panel">
                <button
                  type="button"
                  className="landing-upload-cta"
                  aria-label="Generate"
                  disabled={submitting}
                  onClick={handleGenerate}
                >
                  {submitting ? (
                    <span className="landing-upload-cta-spinner" aria-hidden="true" />
                  ) : (
                    <span aria-hidden="true">→</span>
                  )}
                </button>

                <div style={{ position: 'relative' }}>
                  <textarea
                    className="landing-upload-input"
                    placeholder="Add instructions — e.g. “Recreate this hook for my product” (optional)…"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    onKeyDown={(e) => {
                      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') handleGenerate();
                    }}
                    rows="5"
                    autoFocus
                  />
                </div>

                <div className="landing-bottom-actions">
                  <button
                    type="button"
                    className="landing-attach-button"
                    onClick={() => fileInputRef.current?.click()}
                    aria-label="Attach files"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                    </svg>
                  </button>

                  <DropdownMenu.Root>
                    <DropdownMenu.Trigger asChild>
                      <button type="button" className="ar-trigger" aria-label="Aspect ratio">
                        <AspectRatioIcon
                          w={ASPECT_RATIO_OPTIONS.find((o) => o.value === aspectRatio)?.w ?? 9}
                          h={ASPECT_RATIO_OPTIONS.find((o) => o.value === aspectRatio)?.h ?? 16}
                          size={16}
                        />
                        <span className="ar-trigger-label">
                          {ASPECT_RATIO_OPTIONS.find((o) => o.value === aspectRatio)?.label ?? aspectRatio}
                        </span>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="ar-chevron">
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Portal>
                      <DropdownMenu.Content className="ar-content cm-dropdown" sideOffset={6} align="start">
                        {ASPECT_RATIO_OPTIONS.map(({ value, label, w, h }) => (
                          <DropdownMenu.Item
                            key={value}
                            className={`ar-item${aspectRatio === value ? ' ar-item-selected' : ''}`}
                            onSelect={() => setAspectRatio(value)}
                          >
                            <AspectRatioIcon w={w} h={h} size={16} />
                            <span className="ar-item-label">{label ?? value}</span>
                            {aspectRatio === value && (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="ar-check">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            )}
                          </DropdownMenu.Item>
                        ))}
                      </DropdownMenu.Content>
                    </DropdownMenu.Portal>
                  </DropdownMenu.Root>

                  <DropdownMenu.Root>
                    <DropdownMenu.Trigger asChild>
                      <button type="button" className="mt-trigger" aria-label="Model tier">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="mt-trigger-icon">
                          <path d="M12 2L2 7l10 5 10-5-10-5z" />
                          <path d="M2 17l10 5 10-5" />
                          <path d="M2 12l10 5 10-5" />
                        </svg>
                        <span className="mt-trigger-label">
                          {MODEL_TIER_OPTIONS.find((o) => o.value === modelTier)?.name ?? 'Fast'}
                        </span>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="ar-chevron">
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Portal>
                      <DropdownMenu.Content className="mt-content cm-dropdown" sideOffset={6} align="start">
                        {MODEL_TIER_OPTIONS.map((option) => (
                          <DropdownMenu.Item
                            key={option.value}
                            className={`mt-item${modelTier === option.value ? ' mt-item-selected' : ''}`}
                            onSelect={() => setModelTier(option.value)}
                          >
                            <div className="mt-item-header">
                              <span className="mt-item-name">{option.name}</span>
                              {modelTier === option.value && (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="mt-check">
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                              )}
                            </div>
                            <p className="mt-item-desc">{option.description}</p>
                            <div className="mt-item-stats">
                              <span className="mt-stat">
                                <span className="mt-stat-label">Speed</span>
                                <RatingDots value={option.speed} />
                              </span>
                              <span className="mt-stat">
                                <span className="mt-stat-label">Cost</span>
                                <RatingDots value={option.cost} />
                              </span>
                            </div>
                          </DropdownMenu.Item>
                        ))}
                      </DropdownMenu.Content>
                    </DropdownMenu.Portal>
                  </DropdownMenu.Root>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.md,.markdown,.txt,.png,.jpg,.jpeg,.mp4,.mov,.webm,.mp3,.wav,.m4a"
                  onChange={handleFileInput}
                  style={{ display: 'none' }}
                  aria-label="File input"
                />

                {showGrid && (
                  <div className="landing-attachments-grid">
                    {includeVideo && mediaUrl && (
                      <div className="landing-pdf-chip">
                        <span className="landing-pdf-extension">{reel.video_url ? 'MP4' : 'IMG'}</span>
                        <div className="landing-pdf-info">
                          <span className="landing-pdf-name">{reel.video_url ? 'Reel video' : 'Reel thumbnail'}</span>
                          <span className="landing-pdf-size">{reel.username ? `@${reel.username}` : 'attached'}</span>
                        </div>
                        <button type="button" className="landing-pdf-remove" onClick={() => setIncludeVideo(false)} aria-label="Remove video">×</button>
                      </div>
                    )}

                    {includeAnalysis && hasAnalysis && (
                      <div className="landing-pdf-chip" title="The analysis is attached and sent with your prompt, but not shown here.">
                        <span className="landing-pdf-extension">TXT</span>
                        <div className="landing-pdf-info">
                          <span className="landing-pdf-name">Video analysis</span>
                          <span className="landing-pdf-size">auto-generated</span>
                        </div>
                        <button type="button" className="landing-pdf-remove" onClick={() => setIncludeAnalysis(false)} aria-label="Remove analysis">×</button>
                      </div>
                    )}

                    {attached.map((a) => (
                      <div key={a.id} className="landing-pdf-chip">
                        <span className="landing-pdf-extension">{a.extension}</span>
                        <div className="landing-pdf-info">
                          <span className="landing-pdf-name">{a.name}</span>
                          <span className="landing-pdf-size">{formatFileSize(a.size)}</span>
                        </div>
                        <button type="button" className="landing-pdf-remove" onClick={() => setAttached((prev) => prev.filter((x) => x.id !== a.id))} aria-label="Remove attachment">×</button>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {error && <div className="cm-error">{error}</div>}
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
